import React from 'react';
import { useTranslation } from 'react-i18next';
import { openCookieSettings } from '../content/legal';

/**
 * The footer at the bottom of every logged-in page.
 *
 * Variant C designs this as `.page-footer` and its demo appends one to whatever
 * page just mounted, with a comment saying the app "carries one Footer at the
 * root". The app had stopped carrying one: the old Footer component was still
 * styled with utility classes from before this design existed, nothing rendered
 * it, and Stage 16's dead-code pass deleted it — which is how a designed part of
 * the page came to be missing rather than restyled. This is that footer, built
 * from the demo's own markup.
 *
 * The links go where the app already keeps these: the two legal documents are
 * static pages in /public, pricing is the in-app plan page, and cookie settings
 * re-open the consent banner (the same control the landing page's footer uses).
 * Reporting a bug is a signed-in action, so it only appears when there is
 * somewhere for the report to come from.
 */
const AppFooter = React.memo(function AppFooter({ onOpenPlan, onOpenBugReport, canReportBug = false }) {
    const { t } = useTranslation();

    return (
        <footer className="page-footer">
            <div className="links">
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                    {t('auth.privacyPolicy')}
                </a>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer">
                    {t('auth.termsOfService')}
                </a>
                {onOpenPlan && (
                    <button type="button" className="link" onClick={onOpenPlan}>
                        {t('landing.footer.pricing')}
                    </button>
                )}
                <button type="button" className="link" onClick={openCookieSettings}>
                    {t('landing.footer.cookieSettings')}
                </button>
                {canReportBug && onOpenBugReport && (
                    <button type="button" className="link" onClick={onOpenBugReport}>
                        {t('drawer.reportBug')}
                    </button>
                )}
            </div>
            {t('footer.copyright', { year: new Date().getFullYear() })}
            {' · '}
            {t('footer.disclaimer')}
        </footer>
    );
});

export default AppFooter;
